import { unstable_cache } from 'next/cache';
import z from 'zod';

import { Params } from '@/app/used-cars/_components/search/search-results/search-params';
import { otherOptions } from '@/constants/filters/filters.constants';
import {
  getDynamicVehicleFilter,
} from '@/constants/used-cars/used-cars.constants';
import { getRevalidateTimeout } from '@/utils/cache';

import { directusService, zodKeys } from '../api.service';
import { DateService } from '../date.service';
import {
  ceilMileageToFiftyThousand,
  floorMileageToFiftyThousand,
} from '../string-format.service';

/**
 * Brand / range
 */
const VehicleSchema = z.object({
  id: z.number(),
});

const RangeSchema = z.object({
  id: z.number(),
  name: z.string(),
  vehicles: z.array(VehicleSchema).optional(),
});

const BrandSchema = z.object({
  id: z.number(),
  name: z.string(),
  vehicles: z.array(VehicleSchema).optional(),
  range_ids: z.array(RangeSchema),
});

const BrandAndRangesSchema = z.array(BrandSchema);

export type BrandAndRangesType = z.infer<typeof BrandAndRangesSchema>;
export type BrandType = z.infer<typeof BrandSchema>;
export type RangeType = z.infer<typeof RangeSchema>;

export const getBrandsAndRanges = async (
  _vehicleType?: Params['type'],
  _dealershipId?: string
) => {
  const { data } = await directusService.get('/items/brands', {
    params: {
      sort: 'name',
      fields: [
        'id',
        'name',
        'range_ids.id',
        'range_ids.name',
      ].join(','),
      deep: {
        range_ids: {
          sort: 'name',
          _limit: 50,
        },
      },
    },
  });

  const parsedData = BrandAndRangesSchema.parse(data);

  return parsedData;
};

export const fetchBrandsAndRanges = (
  vehicleType?: Params['type'],
  dealershipId?: string,
) =>
  unstable_cache(
    async () => getBrandsAndRanges(vehicleType, dealershipId),
    ['brands-and-ranges', vehicleType ?? 'all', dealershipId ?? ''],
    {
      tags: ['brands-and-ranges'],
      revalidate: getRevalidateTimeout(300),
    },
  )();

/**
 * Prices
 */
const PricesSchema = z.array(
  z.object({
    display_price: z.coerce.number(),
  }),
);

const getMaxPrice = async (vehicleType?: Params['type']) => {
  const { data } = await directusService.get('/items/vehicles', {
    params: {
      fields: ['display_price'].join(','),
      filter: getDynamicVehicleFilter(vehicleType),
      sort: '-display_price',
      limit: 1,
    },
  });

  const prices = PricesSchema.parse(data);
  const roundToNearest = 100000;

  return (
    Math.ceil((prices[0]?.display_price || 2000000) / roundToNearest) *
    roundToNearest
  );
};

export const fetchMaxPrice = unstable_cache(getMaxPrice, ['max-price'], {
  tags: ['max-price'],
  revalidate: getRevalidateTimeout(300),
});

/**
 * Body types
 */
const BodyTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.union([z.string(), z.null()]),
  vehicles: z.array(VehicleSchema),
  isDisabled: z.boolean().default(false),
  count: z.number().int().nonnegative().default(0),
});
const BodyTypeListSchema = z.array(BodyTypeSchema);

export type BodyType = z.infer<typeof BodyTypeSchema>;
export type BodyTypeList = z.infer<typeof BodyTypeListSchema>;

export const getBodyTypes = async (
  vehicleType?: Params['type'],
  dealershipId?: string,
) => {
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);

  const flatFilter = {
    _and: [
      ...Object.entries(dynamicFilter).map(([key, value]) => ({
        [key]: value,
      })),
      { status: { _eq: 'active' } },
      { is_hide_on_website: { _neq: true } },
    ],
  };

  if (dealershipId) {
    (flatFilter._and as any[]).push({
      dealership_id: { _eq: String(dealershipId) },
    });
  }

  const { data } = await directusService.get('/items/spec_body_types', {
    params: {
      fields: [
        'id',
        'name',
        'image',
        'vehicles.id',
        'vehicles.status',
        'vehicles.is_hide_on_website',
      ].join(','),
      deep: {
        vehicles: {
          _limit: -1,
          _filter: flatFilter,
        },
      },
    },
  });

  const bodyTypes = BodyTypeListSchema.parse(data);

  const bodyTypesWithDisabledState = bodyTypes
    ?.filter((type) => Boolean(type?.image))
    .map((bodyType) => ({
      ...bodyType,
      isDisabled: !(bodyType?.vehicles?.length > 0),
      count: bodyType?.vehicles?.length,
    }));

  return bodyTypesWithDisabledState;
};

export const fetchBodyTypes = unstable_cache(
  getBodyTypes,
  ['body-types'],
  {
    tags: ['body-types', 'vehicles-search'],
    revalidate: 60 * 60, 
  },
);

const BrandNameSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  range_ids: z.array(
    z.object({
      id: z.coerce.string(),
      name: z.string(),
    }),
  ).optional(),
});
const BrandNamesSchema = z.array(BrandNameSchema);
const getBrandBySlug = async (
  slug: string,
  _type?: string,
  _dealershipId?: string,
) => {
  // Ultra-simple query structure - only basic filtering
  const { data } = await directusService.get('/items/brands', {
    params: {
      filter: {
        name: {
          _icontains: slug,
        },
      },
      deep: {
        range_ids: {
          sort: 'name',
          _limit: 50,
        },
      },
      fields: ['id', 'name', 'range_ids.name', 'range_ids.id'].join(','),
      limit: 1,
    },
  });

  return BrandNamesSchema.parse(data)[0];
};

export const fetchBrandBySlug = unstable_cache(getBrandBySlug, ['brand'], {
  tags: ['brand'],
  revalidate: getRevalidateTimeout(3600),
});

const SpecFilterTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const ColourTypeSchema = SpecFilterTypeSchema.extend({
  colour: z.string(),
  vehicles: z.array(VehicleSchema),
  isDisabled: z.boolean().default(false),
  count: z.number().int().nonnegative().default(0),
});
const ColourTypeListSchema = z.array(ColourTypeSchema);

export type ColourType = z.infer<typeof ColourTypeSchema>;
export type ColourTypeList = z.infer<typeof ColourTypeListSchema>;

const getColourTypes = async (vehicleType?: Params['type']) => {
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);
  const flatFilter = {
    _and: Object.entries(dynamicFilter).map(([key, value]) => ({
      [key]: value,
    })),
  };

  const { data } = await directusService.get('/items/spec_body_colors', {
    params: {
      fields: [
        'id',
        'name',
        'colour',
        'vehicles.id',
        'vehicles.status',
        'vehicles.is_hide_on_website',
      ].join(','),
      deep: {
        vehicles: {
          _limit: -1,
          _filter: flatFilter,
        },
      },
    },
  });

  const colorTypes = ColourTypeListSchema.parse(data);

  const colorTypeWithState = colorTypes.map((colorType) => ({
    ...colorType,
    isDisabled: !(colorType?.vehicles?.length > 0),
    count: colorType?.vehicles?.length,
  }));

  return colorTypeWithState;
};

export const fetchColourTypes = unstable_cache(
  getColourTypes,
  ['colour-types'],
  {
    tags: ['colour-types', 'vehicles-search'], 
    revalidate: 60 * 60,
  },
);

const DoorTypeSchema = SpecFilterTypeSchema.extend({
  isDisabled: z.boolean().default(false),
  count: z.number().int().nonnegative().default(0),
});

export type DoorType = z.infer<typeof DoorTypeSchema>;
const DoorTypeListSchema = z.array(DoorTypeSchema);

export type DoorTypeList = z.infer<typeof DoorTypeListSchema>;

const getDoorTypes = async (vehicleType?: Params['type']) => {
  const { data: doorsData } = await directusService.get('/items/spec_doors', {
    params: {
      fields: ['id', 'name'].join(','),
    },
  });

  const doorTypes = DoorTypeListSchema.parse(doorsData);
  const doorVehicleCountMap: Record<number, number> = {};
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);

  const baseVehiclesFilter = {
    _and: Object.entries(dynamicFilter).map(([key, value]) => ({
      [key]: value,
    })),
  };

  for (const door of doorTypes) {
    const vehiclesFilterForDoor = {
      ...baseVehiclesFilter,
      _and: [
        ...baseVehiclesFilter._and,
        { spec_doors: { _nnull: true } },
        { spec_doors: door.id },
      ],
    };

    const { meta } = await directusService.get('/items/vehicles', {
      params: {
        filter: vehiclesFilterForDoor,
        fields: 'id',
        meta: 'filter_count,total_count',
      },
    });

    doorVehicleCountMap[door.id] = meta?.filter_count || 0;
  }

  return doorTypes?.map((door) => ({
    ...door,
    isDisabled: !doorVehicleCountMap[door.id],
    count: doorVehicleCountMap[door.id] || 0,
  }));
};

export const fetchDoorTypes = unstable_cache(getDoorTypes, ['door-types'], {
  tags: ['door-types', 'vehicles-search'], 
  revalidate: 60 * 60,
});

const YearMileageSchema = z.object({
  min: z.coerce.number(),
  max: z.number(),
});

export type YearType = z.infer<typeof YearMileageSchema>;

const getYears = async (vehicleType?: Params['type']) => {
  const { data } = await directusService.get('/items/vehicles', {
    params: {
      fields: 'year',
      sort: 'year',
      limit: 1,
      filter: getDynamicVehicleFilter(vehicleType),
    },
  });

  const returnData = {
    min: data.at(0).year || 0,
    max: DateService.getFullYear(),
  };

  return YearMileageSchema.parse(returnData);
};

export const fetchYears = unstable_cache(getYears, ['years'], {
  tags: ['years'],
  revalidate: getRevalidateTimeout(3600),
});

export type MileageType = z.infer<typeof YearMileageSchema>;

const getMileages = async (vehicleType?: Params['type']) => {
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);

  const { data: minMileage } = await directusService.get('items/vehicles', {
    params: {
      fields: 'mileage',
      sort: 'mileage',
      limit: '1',
      filter: dynamicFilter,
    },
  });

  const { data: maxMileage } = await directusService.get('items/vehicles', {
    params: {
      fields: 'mileage',
      sort: '-mileage',
      limit: '1',
      filter: dynamicFilter,
    },
  });

  const mappedMileage = {
    min: floorMileageToFiftyThousand(minMileage?.at(0)?.mileage) || 0,
    max: ceilMileageToFiftyThousand(maxMileage?.at(0)?.mileage) || 0,
  };

  return YearMileageSchema.parse(mappedMileage);
};

export const fetchMileages = unstable_cache(getMileages, ['mileages'], {
  tags: ['mileages'],
  revalidate: getRevalidateTimeout(3600),
});

const consumptionSchema = z.object({
  id: z.number(),
  float_value: z.number(),
});

export const getConsumption = async () => {
  const { data: minConsumption } = await directusService.get(
    'items/spec_litres_100km',
    {
      params: {
        fields: zodKeys(consumptionSchema),
        sort: 'float_value',
        limit: '1',
      },
    },
  );

  const { data: maxConsumption } = await directusService.get(
    'items/spec_litres_100km',
    {
      params: {
        fields: zodKeys(consumptionSchema),
        sort: '-float_value',
        limit: '1',
      },
    },
  );

  const validatedMinConsumption = z
    .array(consumptionSchema)
    .safeParse(minConsumption);
  const validatedMaxConsumption = z
    .array(consumptionSchema)
    .safeParse(maxConsumption);

  if (!validatedMinConsumption.success) {
    throw new Error('Invalid response format for min consumption');
  }

  if (!validatedMaxConsumption.success) {
    throw new Error('Invalid response format for max consumption');
  }

  return {
    min: validatedMinConsumption.data.at(0)?.float_value ?? null,
    max: validatedMaxConsumption.data.at(0)?.float_value ?? null,
  };
};

export type Consumption = Awaited<ReturnType<typeof getConsumption>>;
export const fetchConsumption = unstable_cache(
  getConsumption,
  ['fuel-consumption'],
  {
    tags: ['fuel-consumption'],
    revalidate: getRevalidateTimeout(3600),
  },
);

/**
 * Provinces + dealerships
 */
const DealershipSchema = z.object({
  id: z.string(),
  name: z.string(),
  city: z
    .object({
      name: z.string(),
    })
    .nullish(),
  vehicles: z.array(VehicleSchema),
  vehicleCount: z.number().int().nonnegative().default(0),
  isOpened: z.boolean().default(false),
  isChecked: z.boolean().default(false),
});
const ProvinceSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  dealerships: z.array(DealershipSchema),
  vehicleCount: z.number().int().nonnegative().default(0),
  isOpened: z.boolean().default(false),
  isChecked: z.boolean().default(false),
});
const ProvinceListSchema = z.array(ProvinceSchema);

export const getDealershipsByProvince = async (
  vehicleType?: Params['type'],
  dealershipId?: string,
) => {
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);

  const flatFilter = {
    _and: [
      ...Object.entries(dynamicFilter).map(([key, value]) => ({
        [key]: value,
      })),
      { status: { _eq: 'active' } },
      { is_hide_on_website: { _neq: true } },
    ],
  };

  const nestedFilter = {
    _and: [
      ...Object.entries(dynamicFilter).map(([key, value]) => ({
        dealerships: { vehicles: { [key]: value } },
      })),
    ],
  };

  if (dealershipId) {
    (nestedFilter._and as any[]).push({
      dealerships: { id: { _eq: String(dealershipId) } },
    });
  }

  const { data } = await directusService.get('/items/provinces', {
    params: {
      sort: 'name',
      fields: [
        'id',
        'name',
        'dealerships.id',
        'dealerships.name',
        'dealerships.status',
        'dealerships.city.name',
        'dealerships.vehicles.id',
        'dealerships.vehicles.status',
        'dealerships.vehicles.is_hide_on_website',
      ].join(','),
      filter: {
        ...nestedFilter,
      },
      deep: {
        dealerships: {
          _filter: {
            status: 'published',
            ...(dealershipId && { id: { _eq: String(dealershipId) } }),
            'count(vehicles)': {
              _gt: 0,
            },
          },
          _limit: -1,
          vehicles: {
            _filter: flatFilter,
            _limit: -1,
          },
        },
      },
    },
  });

  let parsedData = ProvinceListSchema.parse(data);

  const provinceDealershipData = parsedData?.map((province) => {
    let provinceVehicleCount = 0;
    const updatedDealerships = province?.dealerships?.map((dealership) => {
      const dealershipVehicleCount = dealership?.vehicles?.length;

      provinceVehicleCount += dealershipVehicleCount;
      return {
        id: dealership.id,
        name: dealership.name,
        // Remove isChecked logic - always show all options like body types
        isDisabled: !(dealershipVehicleCount > 0),
        vehicleCount: dealershipVehicleCount,
        city: dealership.city,
      };
    });

    return {
      id: province.id,
      name: province.name,
      // Remove isChecked logic - always show all options like body types  
      isDisabled: !(provinceVehicleCount > 0),
      dealerships: updatedDealerships,
      children: updatedDealerships,
      vehicleCount: provinceVehicleCount,
    };
  });

  return provinceDealershipData;
};

export type DealershipsByProvince = Awaited<
  ReturnType<typeof getDealershipsByProvince>
>;

export const fetchDealershipsByProvince = unstable_cache(
  getDealershipsByProvince,
  ['dealerships-by-province'],
  {
    tags: ['dealerships-by-province'],
    revalidate: getRevalidateTimeout(3600),
  },
);

const TransmissionTypeSchema = z.object({
  id: z.number(),
  name: z
    .string()
    .transform((val) => val.charAt(0).toUpperCase() + val.slice(1)),
  value: z.string(),
  isSelected: z.boolean().default(false),
  isDisabled: z.boolean().default(false),
});

export type TransmissionType = z.infer<typeof TransmissionTypeSchema>;

export const getTransmissionTypes = async (vehicleType: Params['type']) => {
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);
  const staticTransmissionTypes = [
    {
      id: 2,
      name: 'Manual',
      value: 'manual',
      isSelected: false,
      isDisabled: false,
    },
    {
      id: 1,
      name: 'Automatic',
      value: 'automatic',
      isSelected: false,
      isDisabled: false,
    },
  ];

  const driveTypePromises = staticTransmissionTypes?.map(
    async (transmissionType) => {
      const vehicleCheck = await directusService.get('/items/vehicles', {
        params: {
          fields: ['id'],
          filter: {
            spec_transmission: {
              value: {
                _icontains: transmissionType?.value,
              },
            },
            ...dynamicFilter,
          },
          meta: 'filter_count,total_count',
          limit: 1,
        },
      });

      return {
        ...transmissionType,
        isDisabled: vehicleCheck?.meta?.filter_count === 0,
        count: vehicleCheck?.meta?.filter_count,
      };
    },
  );

  return Promise.all(driveTypePromises);
};

export type TransmissionTypeList = Awaited<
  ReturnType<typeof getTransmissionTypes>
>;
export const fetchTransmissionTypes = unstable_cache(
  getTransmissionTypes,
  ['transmission-types'],
  {
    tags: ['transmission-types'],
    revalidate: getRevalidateTimeout(3600),
  },
);

const DriveTypeSchema = z.object({
  id: z.number(),
  value: z.string(),
  isSelected: z.boolean().default(false),
  isDisabled: z.boolean().default(false),
  count: z.number().int().nonnegative().default(0),
});
const DriveTypeListSchema = z.array(DriveTypeSchema);

export type DriveType = z.infer<typeof DriveTypeSchema>;

const getDriveTypes = async (vehicleType: Params['type']) => {
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);
  const driveTypesResponse = await directusService.get(
    '/items/spec_drivetrain',
    {
      params: {
        fields: ['id', 'value'].join(','),
      },
    },
  );

  const validatedDriveTypes = DriveTypeListSchema.parse(
    driveTypesResponse.data,
  );

  if (!validatedDriveTypes.length) {
    return null;
  }

  const driveTypePromises = validatedDriveTypes.map(async (driveType) => {
    const vehicleCheck = await directusService.get('/items/vehicles', {
      params: {
        fields: ['id'],
        filter: {
          spec_drivetrain: {
            _eq: driveType.id,
          },
          ...dynamicFilter,
        },
        meta: 'filter_count,total_count',
        limit: 1,
      },
    });

    return {
      ...driveType,
      isDisabled: vehicleCheck?.meta?.filter_count === 0,
      count: vehicleCheck?.meta?.filter_count,
    };
  });

  return Promise.all(driveTypePromises);
};

export type DriveTypeList = Awaited<ReturnType<typeof getDriveTypes>>;

export const fetchDriveTypes = unstable_cache(getDriveTypes, ['drive-types'], {
  tags: ['drive-types'],
  revalidate: getRevalidateTimeout(3600),
});

// const FuelTypeSchema = z.object({
//   id: z.number(),
//   value: z.string(),
//   name: z.string(),
//   isSelected: z.boolean().default(false),
//   isDisabled: z.boolean().default(false),
//   count: z.number().int().nonnegative().default(0),
// });

// type FuelType = z.infer<typeof FuelTypeSchema>;
// const FuelTypeListSchema = z.array(FuelTypeSchema);

// const groupByFuelTypeName = (fuelTypes: FuelType[]): FuelType[] => {
//   const grouped = fuelTypes.reduce<Record<string, FuelType>>((acc, curr) => {
//     if (curr.isDisabled) {
//       return acc;
//     }
//     const existing = acc[curr.name];

//     if (existing) {
//       existing.value += `,${curr.value}`;
//       existing.isDisabled = existing.isDisabled && curr.isDisabled;
//     } else {
//       acc[curr.name] = { ...curr };
//     }
//     return acc;
//   }, {});

//   return Object.values(grouped);
// };

const getFuelTypes = async () => {
  const staticFuelTypes = [
    { id: 1, name: 'Petrol', value: 'Petrol',  isSelected: false,
      isDisabled: false, },
    { id: 2, name: 'Diesel', value: 'Diesel',  isSelected: false,
      isDisabled: false, },
    { id: 9, name: 'Petrol-electric hybrid', value: 'Petrol-electric hybrid', isSelected: false,
      isDisabled: false, },
    { id: 3, name: 'Electric', value: 'Electric', isSelected: false,
      isDisabled: false, },
  ];

  return staticFuelTypes;
};

export type FuelTypeList = Awaited<ReturnType<typeof getFuelTypes>>;

export const fetchFuelTypes = unstable_cache(getFuelTypes, ['fuel-types'], {
  tags: ['fuel-types'],
  revalidate: getRevalidateTimeout(3600),
});

const FeatureTypeSchema = z.object({
  id: z.number(),
  spec_features: z.array(
    z.object({
      spec_features_id: z.number(),
    }),
  ),
});

export type FeatureType = z.infer<typeof FeatureTypeSchema>;
const FeatureTypeListSchema = z.array(FeatureTypeSchema);
const getFeatures = async (vehicleType: Params['type']) => {
  const dynamicFilter = getDynamicVehicleFilter(vehicleType);
  const { data } = await directusService.get('items/vehicles', {
    params: {
      fields: zodKeys(FeatureTypeListSchema),
      filter: {
        spec_features: {
          spec_features_id: {
            _in: otherOptions.map((item) => item.id),
          },
        },
        ...dynamicFilter,
      },
      limit: -1,
    },
  });

  const result = FeatureTypeListSchema.parse(data);

  return otherOptions.map((feature) => ({
    count: result.filter((vehicle) =>
      vehicle.spec_features.find(
        (spec) => spec.spec_features_id === feature.id,
      ),
    ).length,
    ...feature,
  }));
};

export type FeatureTypeList = Awaited<ReturnType<typeof getFeatures>>;
export const fetchFeatures = unstable_cache(getFeatures, ['features'], {
  tags: ['features'],
  revalidate: getRevalidateTimeout(3600),
});
